import { useMutation, useQuery } from "@tanstack/react-query";
import { api } from "@/api/client";
import type { components } from "@/api/types";
import type { ReportType } from "@/lib/reports";
import { saveCsv } from "@/lib/download";
import { trackEvent, AnalyticsEvent } from "@/analytics";

/**
 * One of the four deterministic V1 report types (PR-10C), assembled
 * server-side at `/v1/incidents/{id}/reports/{type}`. Distinct from the legacy
 * free-form AI draft at `/incidents/{id}/report` (singular).
 */
export function useIncidentTypedReport(
  incidentId: string | undefined,
  reportType: ReportType,
) {
  return useQuery({
    queryKey: ["incident-typed-report", incidentId, reportType],
    queryFn: () =>
      api.get<components["schemas"]["GeneratedReportRead"]>(
        `/incidents/${incidentId}/reports/${reportType}`,
      ),
    enabled: !!incidentId,
  });
}

const CSV_HEADER = [
  // Lead with incident_id: it is always present, so the export never has a blank
  // identifier column (incident_number is usually null until CAD assigns one).
  "incident_id",
  "incident_number",
  "status",
  "incident_type",
  "dispatched_at",
  "cleared_at",
  "address",
] as const;

/**
 * Incident CSV export. The incident list is already department-scoped by the
 * API + RLS, so the CSV is built client-side from `/incidents` filtered to the
 * dispatched-at date window — no export job or extra endpoint needed. Both
 * bounds are optional: an open-ended range exports everything up to / from the
 * given day, or the full history when neither is set.
 */
export function useIncidentsCsvExport() {
  return useMutation({
    mutationFn: async (range: {
      from?: string;
      to?: string;
    }): Promise<{ count: number; filename: string }> => {
      const incidents =
        await api.get<components["schemas"]["IncidentRead"][]>("/incidents");
      const inRange = incidents.filter((i) => {
        const day = i.dispatched_at.slice(0, 10);
        return (
          (!range.from || day >= range.from) && (!range.to || day <= range.to)
        );
      });
      const rows: string[][] = [
        [...CSV_HEADER],
        ...inRange.map((i) => [
          i.id,
          i.incident_number ?? "",
          i.status,
          i.incident_type ?? "",
          i.dispatched_at,
          i.cleared_at ?? "",
          i.address ?? "",
        ]),
      ];
      const filename = `incidents-${range.from || "all"}-to-${range.to || "all"}.csv`;
      saveCsv(rows, filename);
      return { count: inRange.length, filename };
    },
    onSuccess: (result) => {
      trackEvent(AnalyticsEvent.IncidentsCsvExported, {
        count: result.count,
      });
    },
  });
}

export function useComplianceArchive(
  params: {
    from?: string;
    to?: string;
  } = {},
) {
  return useQuery({
    queryKey: ["compliance-archive", params],
    queryFn: () =>
      api.get<components["schemas"]["ComplianceArchivePage"]>(
        "/compliance/archive",
        { query: { from: params.from, to: params.to } },
      ),
  });
}

/**
 * Bulk-download a ZIP of every finalized report PDF in the date window. The API
 * streams `application/zip`, so this goes through the authenticated blob
 * download helper rather than the JSON client.
 */
export function useBulkComplianceDownload() {
  return useMutation({
    mutationFn: (range: { from: string; to: string }) =>
      api.download("/compliance/archive.zip", {
        query: { from: range.from, to: range.to },
        filename: `compliance-archive-${range.from}-to-${range.to}.zip`,
      }),
    onSuccess: () => {
      trackEvent(AnalyticsEvent.ComplianceArchiveDownloaded);
    },
  });
}
